import { useCallback, useMemo } from "react";
import { t } from "ttag";
import * as Yup from "yup";

import { useGetSms2faQuery, useUpdateSms2faMutation } from "metabase/api";
import {
  Form,
  FormErrorMessage,
  FormProvider,
  FormSubmitButton,
  FormTextInput,
} from "metabase/forms";
import { Alert, Group, Stack, Text } from "metabase/ui";
import type { User } from "metabase-types/api";

// Deliberately permissive: the backend normalises `+972…`, `972…`, `00972…` and `05…` to one
// stored form, and rejects anything it cannot make sense of. Leaving it blank turns 2FA off.
const SMS_2FA_SCHEMA = Yup.object({
  sms_2fa_phone: Yup.string().default(""),
});

export interface Sms2faFormProps {
  user: User;
}

export const Sms2faForm = ({ user }: Sms2faFormProps): JSX.Element => {
  const { data, isLoading } = useGetSms2faQuery(user.id);
  const [updateSms2fa] = useUpdateSms2faMutation();

  const initialValues = useMemo(
    () => ({ sms_2fa_phone: data?.sms_2fa_phone ?? "" }),
    [data?.sms_2fa_phone],
  );

  const handleSubmit = useCallback(
    async ({ sms_2fa_phone }: { sms_2fa_phone: string }) => {
      await updateSms2fa({
        id: user.id,
        sms_2fa_phone: sms_2fa_phone.trim() === "" ? null : sms_2fa_phone,
      }).unwrap();
    },
    [user.id, updateSms2fa],
  );

  if (isLoading) {
    return <></>;
  }

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={SMS_2FA_SCHEMA}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <Form>
        <Stack>
          <Text fw="bold">{t`Configure 2FA with SMS`}</Text>
          <Text c="text-secondary">
            {t`Add your phone number and we'll text you a verification code each time you sign in with a password. Leave it empty to turn two-factor authentication off.`}
          </Text>

          {data?.sms_2fa_available === false && (
            <Alert variant="light" color="warning">
              {t`SMS delivery is not configured on this instance yet, so codes cannot be sent. Ask an administrator to finish setting it up before turning this on.`}
            </Alert>
          )}

          <FormTextInput
            name="sms_2fa_phone"
            label={t`Phone number`}
            placeholder="+972525223961"
            type="tel"
            autoComplete="tel"
          />

          <Group>
            <FormSubmitButton label={t`Update`} variant="filled" />
          </Group>
          <FormErrorMessage />
        </Stack>
      </Form>
    </FormProvider>
  );
};
